import { hexRing, type FleetState, type GameState, type Hex } from '@sd/sim';

/**
 * A synthetic busy map for the render benchmark (#55, GDD: 60 FPS on
 * 1030 hexes and 300 ships). Fleets of five ships fly around orbit rings,
 * one hex per eight ticks, so every fleet moves every frame. Only for the
 * dev tool `?dev=1&bench=<ships>`; no match runs.
 */
export function benchState(base: GameState, ships: number, tick: number): GameState {
  const owners = base.players.map((p) => p.id);
  const fleets: FleetState[] = [];
  const perFleet = 5;
  const count = Math.ceil(ships / perFleet);
  const rings = [4, 7, 10, 13, 16].filter((r) => r <= base.system.mapRadius);
  for (let i = 0; i < count; i++) {
    const ring = hexRing({ q: 0, r: 0 }, rings[i % rings.length]!);
    const start = Math.floor((i * 7 + tick / 8) % ring.length);
    const here: Hex = ring[start]!;
    const next: Hex = ring[(start + 1) % ring.length]!;
    fleets.push({
      id: 10_000 + i,
      owner: owners[i % owners.length]!,
      hex: here,
      path: [next],
      progress: tick % 8,
      scan: null,
      ships: Array.from({ length: Math.min(perFleet, ships - i * perFleet) }, (_, k) => ({
        id: 100_000 + i * perFleet + k,
        cls: k === 0 ? 'cruiser' : 'frigate',
      })),
    });
  }
  return { ...base, tick, fleets };
}

export interface BenchResult {
  readonly ships: number;
  readonly frames: number;
  /** Frames per second the browser managed (bounded by its refresh rate and the GPU). */
  readonly fps: number;
  /** JavaScript time per frame: map update and draw submission, the part we control. */
  readonly cpuMsAvg: number;
  readonly cpuMsP95: number;
  readonly drawCalls: number;
  readonly triangles: number;
}

export class BenchMeter {
  private readonly cpu: number[] = [];
  private first = 0;
  private last = 0;

  constructor(private readonly ships: number) {}

  sample(cpuMs: number, now: number) {
    if (this.cpu.length === 0) this.first = now;
    this.last = now;
    this.cpu.push(cpuMs);
  }

  get frames(): number {
    return this.cpu.length;
  }

  result(drawCalls: number, triangles: number): BenchResult {
    const sorted = [...this.cpu].sort((a, b) => a - b);
    const avg = sorted.reduce((a, b) => a + b, 0) / Math.max(1, sorted.length);
    const seconds = (this.last - this.first) / 1000;
    return {
      ships: this.ships,
      frames: sorted.length,
      fps: Math.round(((sorted.length - 1) / Math.max(seconds, 1e-3)) * 10) / 10,
      cpuMsAvg: Math.round(avg * 100) / 100,
      cpuMsP95: Math.round((sorted[Math.floor(sorted.length * 0.95)] ?? 0) * 100) / 100,
      drawCalls,
      triangles,
    };
  }
}
